import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PageHeader, Panel } from "@/components/AppShell";
import { PayrollCalcCard } from "@/components/PayrollCalcCard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { usePortfolio } from "@/context/ProfileContext";
import { errMsg } from "@/lib/api";
import { annualContribs, PERIODS, tspLimit, LIMITS, project, FUNDS, FUND_META, blendedReturn, employeePct, perPaycheck, withContrib, convertContrib, contribOf } from "@/lib/finance";
import { usd } from "@/lib/format";
import { syncDates } from "@/lib/fers";

export function Field({ label, k, p, set, type = "number", step = "any", hint }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={k}>{label}</Label>
      <Input id={k} type={type} step={step} className="font-mono-num" value={p[k] ?? ""} onChange={(e) => set({ ...p, [k]: type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value })} data-testid={`profile-${k}-input`} />
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function Choice({ label, k, p, set, options }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Select value={p[k]} onValueChange={(v) => set({ ...p, [k]: v })}>
        <SelectTrigger data-testid={`profile-${k}-select`}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function AllocationEditor({ p, set }) {
  const total = FUNDS.reduce((s, f) => s + (Number(p.allocation?.[f]) || 0), 0);
  return (
    <div className="space-y-4" data-testid="allocation-editor">
      {FUNDS.map((f) => (
        <div key={f}>
          <div className="flex justify-between text-sm mb-2"><span className="flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{ background: FUND_META[f].color }} />{FUND_META[f].name}</span><span className="font-mono-num">{p.allocation?.[f] || 0}%</span></div>
          <Slider min={0} max={100} step={5} value={[p.allocation?.[f] || 0]} onValueChange={([v]) => set({ ...p, allocation: { ...p.allocation, [f]: v } })} data-testid={`profile-alloc-${f}-slider`} />
        </div>
      ))}
      <p className={`text-xs font-mono-num ${total === 100 ? "text-emerald-600" : "text-amber-600"}`} data-testid="allocation-total">Total {total}% {total !== 100 && "(normalized when projecting)"} · Blended {blendedReturn(p.allocation).toFixed(2)}%</p>
    </div>
  );
}

export default function Profile() {
  const { profile, saveProfile, dash } = usePortfolio();
  const [p, setP] = useState(null);
  useEffect(() => { if (profile) setP(profile); }, [profile]);
  if (!p) return <p className="text-sm text-muted-foreground">Loading profile...</p>;
  const c = annualContribs(p, Number(p.salary) || 0, Number(p.age) || 0);
  const periods = PERIODS[p.pay_frequency];
  const capped = c.employee >= tspLimit(p.age) - 1;
  const save = async () => {
    try { await saveProfile(p); toast.success("Profile saved. Projections updated."); } catch (e) { toast.error(errMsg(e)); }
  };
  const proj = project(p, dash?.totals?.total || 0);
  return (
    <div>
      <PageHeader title="Profile & Pay Matrix" subtitle="Your age, pay and contribution rules drive every projection. Change anything and the numbers on the right update instantly."
        actions={<Button className="rounded-full" onClick={save} data-testid="profile-save-button">Save profile</Button>} />
      <PayrollCalcCard testid="payroll-calc-card-profile" />
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">
          <Panel title="Timeline" testid="panel-timeline">
            <div className="grid sm:grid-cols-4 gap-4">
              <Field label="Current age" k="age" p={p} set={setP} />
              <Field label="Birth year" k="birth_year" p={p} set={setP} hint="Sets RMD start age" />
              <Field label="Target retirement age" k="retire_age" p={p} set={setP} />
              <Field label="Life expectancy" k="life_expectancy" p={p} set={setP} />
              <Field label="Birth date (optional)" k="birth_date" type="date" p={p} set={(n) => setP(syncDates(n))} hint="Sets age and birth year exactly" />
              <Field label="Service computation date (optional)" k="service_start_date" type="date" p={p} set={(n) => setP(syncDates(n))} hint="Sets service years for pension math" />
            </div>
          </Panel>
          <Panel title="Pay" testid="panel-pay">
            <div className="grid sm:grid-cols-4 gap-4">
              <Field label="Base salary ($/yr)" k="salary" p={p} set={setP} />
              <Choice label="Pay frequency" k="pay_frequency" p={p} set={setP} options={[["biweekly", "Bi-weekly (26)"], ["semimonthly", "Semi-monthly (24)"], ["monthly", "Monthly (12)"]]} />
              <Field label="Annual raise / COLA %" k="salary_growth" p={p} set={setP} />
              <Field label="Step increase % / yr" k="step_increase_pct" p={p} set={setP} />
            </div>
          </Panel>
          <Panel title="Contributions & match" testid="panel-contrib">
            <div className="grid sm:grid-cols-4 gap-4">
              <Choice label="Contribution type" k="contrib_mode" p={p} set={(np) => setP(np.contrib_mode !== p.contrib_mode ? withContrib(np, convertContrib(p, contribOf(p), np.contrib_mode)) : np)} options={[["percent", "% of pay"], ["fixed", "Fixed $ / paycheck"]]} />
              <Field label={p.contrib_mode === "fixed" ? "$ per paycheck" : "Employee %"} k="contrib_value" p={p} set={setP}
                hint={p.contrib_mode === "fixed" ? `≈ ${employeePct(p, Number(p.salary) || 0).toFixed(1)}% of pay` : `≈ ${usd(perPaycheck(p, Number(p.contrib_value) || 0))} per paycheck`} />
              <Field label="Roth TSP share %" k="roth_split" p={p} set={setP} hint="Rest goes Traditional" />
              <Choice label="Retirement system" k="retirement_system" p={p} set={setP} options={[["FERS", "FERS (1% + up to 4%)"], ["BRS", "Military BRS (1% + up to 4%)"], ["CSRS", "CSRS (no match)"], ["civilian", "Civilian / no match"]]} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <Field label="Traditional IRA $ / yr" k="ira_trad_annual" p={p} set={setP} />
              <Field label="Roth IRA $ / yr" k="ira_roth_annual" p={p} set={setP} />
            </div>
          </Panel>
          <Panel title="Future contribution allocation" testid="panel-allocation-editor"><AllocationEditor p={p} set={setP} /></Panel>
        </div>
        <Panel title="Pay matrix" className="h-fit xl:sticky xl:top-24" testid="panel-pay-matrix">
          <dl className="space-y-3 text-sm">
            {[["Per paycheck (you)", usd(c.employee / periods, 2)], ["Per paycheck (agency)", usd(c.agency / periods, 2)], ["Annual employee", usd(c.employee)], ["  · Traditional", usd(c.employee * (1 - p.roth_split / 100))], ["  · Roth", usd(c.employee * (p.roth_split / 100))], ["Annual agency match", usd(c.agency)], ["Annual IRA", usd(c.ira)], ["Total annual savings", usd(c.total)], ["Effective rate", `${c.employeePct.toFixed(1)}% + ${((c.agency / (p.salary || 1)) * 100).toFixed(1)}% match`]].map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-border/60 pb-2"><dt className="text-muted-foreground whitespace-pre">{k}</dt><dd className="font-mono-num font-medium" data-testid={`matrix-${k.trim().toLowerCase().replace(/[^a-z]+/g, "-")}`}>{v}</dd></div>
            ))}
          </dl>
          {c.employeePct < 5 && (p.retirement_system === "FERS" || p.retirement_system === "BRS") && <p className="mt-4 text-xs text-amber-700 dark:text-amber-400" data-testid="match-warning">You're below 5%, so you're leaving agency match on the table.</p>}
          {capped && <p className="mt-4 text-xs text-amber-700 dark:text-amber-400" data-testid="limit-warning">Capped at the {LIMITS.year} IRS limit of {usd(tspLimit(p.age))}{p.age >= 50 ? " (incl. catch-up)" : ""}.</p>}
          <div className="mt-6 rounded-md bg-slate-900/[0.03] dark:bg-white/5 p-4">
            <p className="text-xs text-muted-foreground">Projected at {p.retire_age} (expected)</p>
            <p className="font-mono-num text-2xl font-semibold text-grad-money mt-1" data-testid="profile-projected-balance">{usd(proj.final.expected)}</p>
          </div>
        </Panel>
      </div>
    </div>
  );
}
